import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretRightIcon,
  ListMagnifyingGlassIcon,
  LockSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useLineStockPosition,
  useNeedInsights,
  useOverrideOption,
  useSuggestionDetail,
  type AssignmentLine,
  type GetSuggestionDetailResponse,
  type LineStockLocation,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import { ApprovalStateChip } from './ApprovalStateCell';
import { lineFacts } from '../utils/lineFacts';
import { isLineTab, lineTabFor, type LineTab } from '../utils/lineTabs';
import { LineFactsPanel } from './LineFactsPanel';
import { LinePlanningTab } from './LinePlanningTab';
import { LineSalesTab } from './LineSalesTab';
import { LineSupplyTab } from './LineSupplyTab';
import { LineVendorCostTab } from './LineVendorCostTab';
import { LineRefreshStamp } from './LineRefreshStamp';
import { buildSourceOverride } from './sourceOverride';
import { StockByLocationTable } from './StockByLocationTable';

interface LineDetailSectionProps {
  suggestionId: string;
  /** The selected board line; null while the cart's lines are loading. */
  line: AssignmentLine | null;
  collapsed: boolean;
  onToggle: () => void;
  onOpenPlanningDetails: () => void;
  onClose: () => void;
  tab: LineTab;
  onTabChange: (tab: LineTab) => void;
  /** Lines selected in the table; the footer shows one of them. */
  selectedCount: number;
}

/**
 * Switches the line to a transfer from `warehouseId` through the Source
 * column's own override (GEN-3023): the line stays in its vendor cart and
 * its approval clears.
 */
function useTransferFrom(line: AssignmentLine | null) {
  const gt = useGT();
  const override = useOverrideOption();
  const transferFrom = (warehouseId: string) => {
    if (line === null) return;
    const result = buildSourceOverride({
      line,
      next: warehouseId,
      supplierId: undefined,
      vendorUnitPrice: undefined,
    });
    if (result.kind !== 'save') return;
    override.mutate(result.input, {
      onError: () =>
        toast.error(
          gt('Could not change the source. Try again.', {
            context: 'replenishment source change error',
          }),
        ),
    });
  };
  return { transferFrom, pending: override.isPending };
}

/**
 * The line's stock at every location, with Transfer (the former Stock
 * Elsewhere pop-out): a tab stays open while the buyer steps lines.
 */
function LineElsewhereTab({
  locations,
  line,
}: {
  locations: LineStockLocation[] | undefined;
  line: AssignmentLine | null;
}) {
  const { transferFrom, pending } = useTransferFrom(line);
  if (locations === undefined) return null;
  return (
    <div className="p-2">
      <StockByLocationTable
        locations={locations}
        line={line}
        pending={pending}
        onTransfer={transferFrom}
      />
    </div>
  );
}

const TAB_TRIGGER_CLASS = 'h-6 px-2 py-0 text-xs';

/**
 * Which line the footer shows: SKU, name and destination, its approval
 * state, and how many lines are selected when it is one of several.
 */
function LineIdentity({
  data,
  sku,
  line,
  selectedCount,
}: {
  data:
    | Pick<
        GetSuggestionDetailResponse,
        'productName' | 'destinationWarehouseName'
      >
    | undefined;
  sku: string | null | undefined;
  line: AssignmentLine | null;
  selectedCount: number;
}) {
  const gt = useGT();
  return (
    <>
      {data && (
        <span className="min-w-0 truncate text-muted-foreground">
          <span className="font-mono text-foreground">{sku}</span>{' '}
          {data.productName !== sku && `${data.productName} `}→{' '}
          {data.destinationWarehouseName}
        </span>
      )}
      {line && <ApprovalStateChip line={line} />}
      {selectedCount > 1 && (
        <span className="shrink-0 text-muted-foreground">
          {gt('1 of {count} selected', {
            context:
              'buying board line detail: the footer shows one of the selected lines',
            count: selectedCount.toLocaleString(),
          })}
        </span>
      )}
    </>
  );
}

/**
 * The bottom section of the buyer board: the selected line's facts in one
 * condensed pane that follows the selection (GEN-2944), tabbed (GEN-3285):
 * At a glance first, then the read-only planning inputs and the item's stock
 * at other warehouses. Planning Details and Forecast Detail stay one click
 * away in the header.
 */
export function LineDetailSection({
  suggestionId,
  line,
  collapsed,
  onToggle,
  onOpenPlanningDetails,
  onClose,
  tab,
  onTabChange,
  selectedCount,
}: LineDetailSectionProps) {
  const gt = useGT();
  // Same query the planning-details modal reads, so opening it is instant.
  const { data } = useSuggestionDetail({ suggestionId }, true);
  const { data: insights } = useNeedInsights({ suggestionId });
  const { data: stock } = useLineStockPosition({ suggestionId });
  const facts = lineFacts({ line, detail: data, insights, stock });
  const Chevron = collapsed ? CaretRightIcon : CaretDownIcon;
  const sku = data?.productSku ?? data?.productV3Key;
  // A legacy line not resolved to a product has no sales or stock to show.
  const noProduct = data !== undefined && data.productV3Key === null;
  return (
    <>
      <div className="flex h-7 shrink-0 items-center gap-2 border-b border-border bg-background px-2 text-xs">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls="buyer-board-line"
          aria-label={gt('Toggle line detail', {
            context: 'buying board line detail fold button',
          })}
          className="flex shrink-0 items-center"
        >
          <Chevron aria-hidden className="h-3 w-3" />
        </button>
        <span className="shrink-0 font-medium">
          <T context="buying board section label, selected line">Line</T>
        </span>
        <LineIdentity
          data={data}
          sku={sku}
          line={line}
          selectedCount={selectedCount}
        />
        <span className="flex-1" />
        <LineRefreshStamp
          engineRun={data?.engineRun}
          asOf={facts.asOf}
          asOfSyncedAt={facts.asOfSyncedAt}
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          onClick={onOpenPlanningDetails}
        >
          <ListMagnifyingGlassIcon aria-hidden className="h-4 w-4" />
          <T context="buying board line detail button">Planning Details</T>
        </Button>
        {data?.productV3Key && (
          <Button
            asChild
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs"
          >
            <Link
              to="/procurement/forecast/details"
              search={{
                sku: data.productV3Key,
                warehouseId: data.destinationWarehouseId,
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowSquareOutIcon aria-hidden className="h-4 w-4" />
              <T context="buying board line forecast detail link">
                Forecast Detail
              </T>
            </Link>
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={onClose}
          aria-label={gt('Close line detail', {
            context: 'buying board line detail close button',
          })}
        >
          <XIcon aria-hidden className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Tabs
        id="buyer-board-line"
        value={lineTabFor(tab, !noProduct)}
        onValueChange={(value) => {
          if (isLineTab(value)) onTabChange(value);
        }}
        className="min-h-0 flex-1 gap-0"
      >
        <TabsList className="h-6 shrink-0 px-1">
          <TabsTrigger value="glance" className={TAB_TRIGGER_CLASS}>
            <T context="buying board line detail tab: the key facts">
              At a glance
            </T>
          </TabsTrigger>
          <TabsTrigger
            value="sales"
            className={TAB_TRIGGER_CLASS}
            disabled={noProduct}
          >
            <T context="buying board line detail tab: sales by month">Sales</T>
          </TabsTrigger>
          <TabsTrigger
            value="supply"
            className={TAB_TRIGGER_CLASS}
            disabled={noProduct}
          >
            <T context="buying board line detail tab: the order lines behind on order and net">
              Supply
            </T>
          </TabsTrigger>
          <TabsTrigger value="planning" className={TAB_TRIGGER_CLASS}>
            <T context="buying board line detail tab: planning inputs, read-only">
              Planning
            </T>
            <LockSimpleIcon aria-hidden className="size-3" />
          </TabsTrigger>
          <TabsTrigger
            value="elsewhere"
            className={TAB_TRIGGER_CLASS}
            disabled={noProduct}
          >
            <T context="buying board line detail tab: the item's stock at other warehouses">
              Elsewhere
            </T>
          </TabsTrigger>
          <TabsTrigger value="vendor" className={TAB_TRIGGER_CLASS}>
            <T context="buying board line detail tab: the vendor's costs and terms">
              Vendor & cost
            </T>
          </TabsTrigger>
        </TabsList>
        <div className="min-h-0 flex-1 overflow-auto">
          <TabsContent value="glance">
            <LineFactsPanel facts={facts} />
          </TabsContent>
          <TabsContent value="sales">
            {data?.productV3Key && (
              <LineSalesTab
                productV3Key={data.productV3Key}
                warehouseId={data.destinationWarehouseId}
                usage={line?.usage ?? null}
              />
            )}
          </TabsContent>
          <TabsContent value="supply">
            <LineSupplyTab
              suggestionId={suggestionId}
              productV3Key={data?.productV3Key}
            />
          </TabsContent>
          <TabsContent value="planning">
            <LinePlanningTab policy={facts.policy} detail={data} />
          </TabsContent>
          <TabsContent value="elsewhere">
            <LineElsewhereTab locations={stock?.locations} line={line} />
          </TabsContent>
          <TabsContent value="vendor">
            <LineVendorCostTab
              line={line}
              options={data?.options}
              chosenOptionId={data?.chosenOptionId}
            />
          </TabsContent>
        </div>
      </Tabs>
    </>
  );
}
